import classNames from "classnames";
import React, {
  ChangeEventHandler,
  FocusEvent,
  FocusEventHandler,
  forwardRef,
  ForwardRefExoticComponent,
  InputHTMLAttributes,
  RefAttributes,
  useState,
} from "react";
import { useElementId } from "../../hooks";
import { FormField, FormFieldProps } from "../form-field";
import { InputTextAlignOption } from "../input";
import { AriaProps } from "../types";
import { CSS_CLASSES } from "./text-area-css-classes";

export interface TextAreaOwnProps
  extends Pick<
    InputHTMLAttributes<HTMLTextAreaElement>,
    | "name"
    | "value"
    | "id"
    | "placeholder"
    | "autoFocus"
    | "maxLength"
    | "minLength"
    | "onKeyDown"
    | "onKeyDownCapture"
    | "onKeyPress"
    | "onKeyPressCapture"
    | "onKeyUp"
    | "onKeyUpCapture"
  > {
  /**
   * If true, render the input component within a FormField.
   *
   * @default false
   */
  formField?: boolean;

  /**
   * Apply/override styling for the FormField wrapping the TextArea component
   * by passing in custom CSS class-name(s) using this prop.
   */
  formFieldClassName?: string;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop. Applied to root element
   */
  className?: string;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop. Applied to actual textarea component
   */
  textAreaClassName?: string;

  /**
   * This prop can be used to pass arbitrary props to the root HTML Div element that this component wraps its inner
   * elements with.
   */
  rootProps?: React.HTMLAttributes<HTMLDivElement>;

  /**
   * Specify how text should be aligned within the underlying TextArea element.
   *
   * @default left
   */
  textAlign?: InputTextAlignOption;

  /**
   * Specify the focus event handler function for the controlled TextArea element.
   */
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;

  /**
   * Specify the blur event handler function for the controlled TextArea element.
   */
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;

  /**
   * Specify the change event handler function for the controlled TextArea element.
   */
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;

  /**
   * Specifies the visible number of lines in a text area
   */
  rows?: number;
}

export type TextAreaToFormFieldPassThroughProps = Omit<
  FormFieldProps,
  keyof TextAreaOwnProps | "formFieldId" | "children" | "focused"
>;

export type TextAreaProps = TextAreaOwnProps & TextAreaToFormFieldPassThroughProps;

/**
 * The `TextArea` tag defines a multi-line text input control.
 *
 * The `TextArea` element is often used in a form, to collect user inputs like comments or reviews.
 *
 * A text area can hold an unlimited number of characters.
 *
 * The size of a text area is specified by the rows attribute
 */
export const TextArea: ForwardRefExoticComponent<TextAreaProps & RefAttributes<HTMLTextAreaElement>> = forwardRef(
  (
    {
      /* Input Base props */
      name,
      value,
      required,
      disabled,
      readOnly,
      textAreaClassName,

      /* Own props */
      id,
      formField,
      className,
      formFieldClassName,
      rootProps,
      placeholder,
      textAlign = "left",
      onFocus,
      onBlur,
      onChange,

      /* FormField pass through props */
      formFieldMessageId,
      label,
      labelInfo,
      hideLabel,
      labelProps,
      secondaryAction,
      hint,
      success,
      error,
      textLength,

      ...restProps
    },
    ref
  ) => {
    const formFieldId = useElementId(id, "text-area");

    const [focused, setFocused] = useState(false);

    const handleFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
      setFocused(true);
      onFocus && onFocus(event);
    };

    const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      onBlur && onBlur(event);
    };

    const rootClassName = classNames(
      {
        [CSS_CLASSES.TEXT_AREA_ROOT]: true,
        [CSS_CLASSES.TEXT_AREA_ROOT_ERROR]: !!error,
        [CSS_CLASSES.TEXT_AREA_ROOT_SUCCESS]: !!success,
        [CSS_CLASSES.TEXT_AREA_ROOT_DISABLED]: !!disabled,
        [CSS_CLASSES.TEXT_AREA_ROOT_TEXT_ALIGN_LEFT]: textAlign === "left",
        [CSS_CLASSES.TEXT_AREA_ROOT_TEXT_ALIGN_CENTER]: textAlign === "center",
        [CSS_CLASSES.TEXT_AREA_ROOT_TEXT_ALIGN_RIGHT]: textAlign === "right",
      },
      className
    );

    const textAreaClasses = classNames(CSS_CLASSES.TEXT_AREA_ELEMENT, textAreaClassName);
    const getTextArea = (ariaProps: AriaProps = {}) => {
      return (
        <div className={rootClassName} {...rootProps}>
          <textarea
            id={formFieldId}
            name={name}
            value={value}
            ref={ref}
            className={textAreaClasses}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={onChange}
            placeholder={placeholder}
            {...ariaProps}
            {...restProps}
          ></textarea>
        </div>
      );
    };

    return formField ? (
      <FormField
        formFieldId={formFieldId}
        formFieldMessageId={formFieldMessageId}
        formFieldType="other"
        label={label}
        labelInfo={labelInfo}
        hideLabel={hideLabel}
        labelProps={labelProps}
        secondaryAction={secondaryAction}
        hint={hint}
        success={success}
        error={error}
        textLength={textLength}
        focused={focused}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        className={formFieldClassName}
      >
        {getTextArea}
      </FormField>
    ) : (
      getTextArea({ "aria-label": label })
    );
  }
);
